/**
 * Blog Article Page Template
 * SEO-optimized article layout
 */

import { useParams, useNavigate, Link } from 'react-router';
import { useSEO, generateBlogSEO, generateArticleStructuredData, useStructuredData } from '../lib/seo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { blogArticleExtensions } from '../data/blogArticleExtensions';
import {
  ArrowLeft,
  Clock,
  Calendar,
  User,
  Share2,
  BookmarkPlus,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { StockHeroStrip, WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { MarketingSiteHeader } from '../components/marketing/MarketingSiteHeader';

// Article database - Expandable for more content
const baseArticles: Record<string, any> = {
  'relationship-anxiety-guide': {
    title: 'The Complete Guide to Relationship Anxiety: Signs, Causes & Solutions',
    description: 'Learn to identify and manage relationship anxiety with science-backed strategies. Understand attachment theory, recognize warning signs, and build healthier connections.',
    author: 'Dr. Sarah Chen',
    publishedAt: '2026-03-10T09:00:00Z',
    modifiedAt: '2026-03-13T10:00:00Z',
    readTime: '12 min',
    tags: ['relationship anxiety', 'attachment theory', 'mental health', 'self-awareness'],
    category: 'Relationship Psychology',
    content: `
## What Is Relationship Anxiety?

Relationship anxiety is a persistent worry or fear about your romantic relationship. Unlike general anxiety, it specifically targets your partnership, causing you to constantly question your relationship's stability, your partner's feelings, or your own worthiness of love.

### Common Signs of Relationship Anxiety

1. **Constant reassurance-seeking** - Frequently asking your partner if they still love you
2. **Overanalyzing texts and conversations** - Reading too much into every word or emoji
3. **Fear of abandonment** - Worrying your partner will leave you
4. **Catastrophizing** - Imagining worst-case scenarios regularly
5. **Difficulty trusting** - Even when your partner has given you no reason not to

## The Science Behind Relationship Anxiety

Research in attachment theory shows that our early childhood experiences shape how we approach adult relationships. If you experienced inconsistent caregiving, abandonment, or emotional unavailability as a child, you're more likely to develop anxious attachment patterns.

### Attachment Styles Explained

**Anxious Attachment**: You crave intimacy but worry your partner doesn't feel the same way. You may become clingy or need constant validation.

**Avoidant Attachment**: You value independence and may emotionally distance yourself when relationships become too close.

**Secure Attachment**: You feel comfortable with intimacy and independence. You trust your partner and communicate openly.

**Disorganized Attachment**: You want closeness but fear it, leading to unpredictable relationship patterns.

## How to Manage Relationship Anxiety

### 1. Practice Self-Awareness

Start by recognizing when anxiety is talking. Use tools like TENSION to track your emotional patterns and identify triggers.

### 2. Challenge Negative Thoughts

When you catch yourself catastrophizing, ask:
- Is there actual evidence for this fear?
- What would I tell a friend in this situation?
- Am I focusing on the worst possible outcome?

### 3. Communicate Openly

Share your anxious thoughts with your partner (without making them responsible for fixing them). Try: "I'm feeling anxious about us, and I wanted to share that with you. It's not about anything you did—it's something I'm working on."

### 4. Build Emotional Independence

Develop a strong sense of self outside your relationship. Pursue hobbies, maintain friendships, and invest in personal growth.

### 5. Seek Professional Support

A therapist specializing in attachment theory can help you understand and heal old wounds that fuel current anxiety.

## TENSION's Role in Managing Relationship Anxiety

TENSION helps you:
- **Track tension patterns** to identify what triggers your anxiety
- **Forecast potential issues** before they escalate
- **Access action cards** with specific strategies for your situation
- **Build awareness** of your emotional responses over time

## When to Seek Help

If relationship anxiety is:
- Interfering with your daily life
- Causing frequent arguments or conflict
- Leading to unhealthy behaviors (checking phone, stalking social media)
- Not improving despite your efforts

...it's time to consult a mental health professional.

## Final Thoughts

Relationship anxiety is common and manageable. With self-awareness, communication, and the right tools, you can build secure, fulfilling relationships despite your anxious tendencies.

Remember: Your anxiety doesn't define you or your relationship's potential.
    `,
  },
};

const articles: Record<string, any> = {
  ...blogArticleExtensions,
  ...baseArticles,
};

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  
  const article = slug ? articles[slug] : null;

  // Keep hooks stable even when the article is missing.
  const resolvedArticle = article ?? {
    title: 'TENSION Blog',
    description: 'Relationship psychology insights and practical guidance from TENSION.',
    author: 'TENSION',
    publishedAt: new Date().toISOString(),
    modifiedAt: new Date().toISOString(),
    tags: ['relationships'],
  };

  // SEO Configuration
  const seoConfig = generateBlogSEO({
    title: resolvedArticle.title,
    description: resolvedArticle.description,
    slug: slug || '',
    author: resolvedArticle.author,
    publishedAt: resolvedArticle.publishedAt,
    modifiedAt: resolvedArticle.modifiedAt,
    tags: resolvedArticle.tags,
  });

  useSEO(seoConfig);

  // Structured Data
  const structuredData = generateArticleStructuredData({
    headline: resolvedArticle.title,
    description: resolvedArticle.description,
    author: resolvedArticle.author,
    publishedAt: resolvedArticle.publishedAt,
    modifiedAt: resolvedArticle.modifiedAt,
  });

  useStructuredData(structuredData);

  // Handle article not found
  if (!article) {
    return (
      <WarmMarketingBackdrop>
        <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-4">
          <div className="w-full max-w-lg">
            <StockHeroStrip stockKey="softAbstract" alt="Decorative abstract imagery" />
          </div>
          <Card className="w-full max-w-md border-violet-100/90 bg-white/95 p-8 text-center shadow-lg">
            <h1 className="mb-4 text-2xl font-bold text-neutral-900">Article Not Found</h1>
            <p className="mb-6 text-neutral-600">
              The article you&apos;re looking for doesn&apos;t exist or has been moved.
            </p>
            <Button onClick={() => navigate('/blog')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
          </Card>
        </div>
      </WarmMarketingBackdrop>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <WarmMarketingBackdrop>
    <div className="min-h-screen">
      <MarketingSiteHeader variant="article" />

      <div className="mx-auto max-w-5xl px-4 pt-6">
        <StockHeroStrip
          stockKey="handsWalking"
          alt="Two people walking together — illustrative imagery for relationship content"
        />
      </div>

      {/* Article Hero */}
      <section className="px-4 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <span className="px-3 py-1 bg-purple-600 text-white text-sm font-semibold rounded-full">
              {article.category}
            </span>
          </div>
          <h1 className="text-5xl font-bold text-neutral-900 mb-6">
            {article.title}
          </h1>
          <p className="text-xl text-neutral-600 mb-8">
            {article.description}
          </p>
          
          {/* Article Meta */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-neutral-600">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {article.author}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {new Date(article.publishedAt).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {article.readTime} read
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-8">
            <Button onClick={handleShare} variant="outline" size="sm">
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
            <Button variant="outline" size="sm">
              <BookmarkPlus className="w-4 h-4 mr-2" />
              Save
            </Button>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div 
            className="prose prose-lg prose-neutral max-w-none
              prose-headings:font-bold prose-headings:text-neutral-900
              prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
              prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
              prose-p:text-neutral-700 prose-p:leading-relaxed prose-p:mb-6
              prose-ul:my-6 prose-li:text-neutral-700
              prose-strong:text-neutral-900 prose-strong:font-semibold
              prose-a:text-purple-600 prose-a:no-underline hover:prose-a:underline"
          >
            {article.content.split('\n').map((line: string, index: number) => {
              if (line.startsWith('## ')) {
                return <h2 key={index}>{line.replace('## ', '')}</h2>;
              } else if (line.startsWith('### ')) {
                return <h3 key={index}>{line.replace('### ', '')}</h3>;
              } else if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={index}><strong>{line.replace(/\*\*/g, '')}</strong></p>;
              } else if (line.trim() === '') {
                return null;
              } else {
                return <p key={index}>{line}</p>;
              }
            })}
          </div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t">
            <h3 className="text-sm font-semibold text-neutral-900 mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
                >
                  #{tag.replace(' ', '')}
                </span>
              ))}
            </div>
          </div>
        </div>
      </article>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-purple-600 to-pink-600">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Track Your Relationship Tension
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Use TENSION to measure, forecast, and stabilize relationship tension with science-backed insights.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/early-access')}
            className="bg-white text-purple-600 hover:bg-neutral-100 font-semibold px-8 py-6 text-lg"
          >
            Start Early Access
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
        </div>
      </section>
    </div>
    </WarmMarketingBackdrop>
  );
}
